import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, PenLine, ShieldAlert } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import SignaturePad from "@/components/SignaturePad";
import { api, errMsg } from "@/lib/api";
import { useSpanish } from "@/lib/translate";
import { STATE_AUTH_TITLE, STATE_AUTH_PARAS } from "@/components/wizard/StepConsent";

export default function SignAuthorization() {
  const { token = "" } = useParams();
  const [info, setInfo] = useState<any>(null);
  const [err, setErr] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [name, setName] = useState("");
  const [sig, setSig] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useSpanish(info?.lang === "es");
  useEffect(() => { api.get(`/public/state-auth/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);

  const submit = async () => {
    setErr("");
    if (!agreed) return setErr("Please read and agree to the State Authorization.");
    if (name.trim().length < 3) return setErr("Please type your full name to sign the State Authorization.");
    if (!sig) return setErr("Please draw your signature on the State Authorization.");
    setBusy(true);
    try { await api.post(`/public/state-auth/${token}`, { agreed, signature_name: name.trim(), signature_image: sig }); setDone(true); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  return (
    <PublicShell>
      <div className="mx-auto max-w-2xl px-4 py-14">
        <div className="card-soft fade-up p-7 sm:p-10" data-testid="sign-auth-card">
          <h1 className="text-3xl font-extrabold tracking-tight">Sign your State Authorization</h1>
          {!info && !err && <Loader2 className="mt-6 h-6 w-6 animate-spin text-[#94A3B8]" />}
          {done ? (
            <div className="mt-6 flex items-start gap-3 rounded-xl bg-[#DCFCE7] px-4 py-3 text-[15px] text-[#166534]" data-testid="sign-auth-done">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> Thank you! Your signed authorization was received. There is nothing else you need to do.
            </div>
          ) : info && (info.closed ? (
            <p className="mt-6 rounded-xl bg-[#F1F5F9] px-4 py-3 text-[15px]" data-testid="sign-auth-closed">This link has expired or the authorization is already signed. If we still need your signature, we'll send you a new email.</p>
          ) : (
            <div className="mt-5 space-y-5">
              <p className="text-[15px] text-[#475569]"><span>{info.lang === "es" ? "Hola" : "Hi"} {info.first_name},</span> <span>please read and sign the authorization below to finish your background screening.</span></p>
              <div className="max-h-96 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 text-[15px] leading-relaxed text-[#334155]" data-testid="sign-auth-text">
                <h3 className="mb-3 text-lg font-bold text-[#1A2536]">{STATE_AUTH_TITLE}</h3>
                {STATE_AUTH_PARAS.map((t) => <p key={t.slice(0, 20)} className="mb-3">{t}</p>)}
                {info.org_name && <p className="text-sm"><b>Organization:</b> {info.org_name}</p>}
              </div>
              <label className="flex cursor-pointer items-start gap-4 rounded-2xl border-2 border-slate-200 bg-white p-5 hover:border-[#1B365D]/40">
                <input type="checkbox" className="mt-1 h-6 w-6 shrink-0 accent-[#1B365D]" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="sign-auth-checkbox" />
                <span className="text-base font-medium text-[#1A2536]">Yes, I have read and agree to this State Authorization.</span>
              </label>
              <label className="block"><span className="field-label">Type your full name to sign the State Authorization</span>
                <input className="field-input font-heading text-lg italic" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-testid="sign-auth-name-input" /></label>
              <div data-testid="sign-auth-signature-pad"><SignaturePad value={sig} onChange={setSig} /></div>
              <button className="btn-primary h-14 w-full text-lg" disabled={busy} onClick={submit} data-testid="sign-auth-submit-button">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><PenLine className="h-5 w-5" /> Sign and send</>}
              </button>
            </div>
          ))}
          {err && <div className="mt-4 flex items-start gap-3 rounded-xl bg-[#FEE2E2] px-4 py-3 text-[15px] text-[#991B1B]" data-testid="sign-auth-error"><ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /> {err}</div>}
        </div>
      </div>
    </PublicShell>
  );
}
